// Página "Análises de canais" — lista TODOS os canais já analisados (Dobra de
// Nicho) com seus dados; clicar num card abre a Dobra daquele canal.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Network, Loader2, Trash2, ArrowLeft, Users, Video, Flame } from 'lucide-react';

import Sidebar from '../Sidebar';
import Header from '../Header';
import PageHeader from '../ui/PageHeader';
import { useSidebar } from '../../contexts/SidebarContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { listAnalyses, hideAnalysis } from '../../lib/dobraNicho';

function fmtNum(n) {
  n = Number(n || 0);
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace('.0', '')}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1).replace('.0', '')}K`;
  return String(n);
}

export default function DobraHistoryPage({ onLogout, isSuperAdmin }) {
  const { t, i18n } = useTranslation();
  usePageTitle(t('dobraNicho.history_title'));
  const { sidebarWidth, isMobile } = useSidebar();
  const navigate = useNavigate();
  const [items, setItems] = useState(null); // null = carregando

  const load = () => { listAnalyses().then(setItems).catch(() => setItems([])); };
  useEffect(() => { load(); }, []);

  const open = (jobId) => navigate(`/dobra-nicho?a=${encodeURIComponent(jobId)}`);

  const hide = async (jobId, e) => {
    e.stopPropagation();
    try {
      await hideAnalysis(jobId);
      setItems((arr) => (arr || []).filter((x) => x.job_id !== jobId));
    } catch {
      toast.error(t('dobraNicho.error_generic'));
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(i18n.language || 'pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return ''; }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} isSuperAdmin={isSuperAdmin} />
      <Header />
      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`, transition: 'margin-left 0.3s ease',
        marginTop: 60, flex: 1, minWidth: 0, animation: 'dp-pageFadeIn 0.35s ease',
      }}>
        <PageHeader
          icon={<Network size={20} style={{ color: 'var(--dp-blue-500)' }} />}
          title={t('dobraNicho.history_title')}
          description={t('dobraNicho.history_subtitle')}
        />

        <button
          onClick={() => navigate('/dobra-nicho')}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dp-blue-500)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'transparent', border: 'none', color: 'var(--dp-text-secondary)', cursor: 'pointer', fontFamily: 'var(--dp-font)', fontSize: 13, marginBottom: 16, transition: 'color 0.15s' }}
        >
          <ArrowLeft size={15} />{t('dobraNicho.back_to_analysis')}
        </button>

        {items === null ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <Loader2 size={32} className="animate-spin" style={{ color: 'var(--dp-blue-500)' }} />
          </div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--dp-text-secondary)' }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: 'rgba(13,174,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Network size={30} style={{ color: 'var(--dp-blue-500)' }} />
            </div>
            <p>{t('dobraNicho.history_empty')}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 14 }}>
            {items.map((it, i) => (
              <motion.div
                key={it.job_id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.3) }}
                whileHover={{ y: -3 }}
                onClick={() => open(it.job_id)}
                style={{ position: 'relative', background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)', borderRadius: 14, padding: 16, cursor: 'pointer', fontFamily: 'var(--dp-font)' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  {it.thumbnail ? (
                    <img src={it.thumbnail} alt="" referrerPolicy="no-referrer" style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid var(--dp-border-subtle)' }} />
                  ) : (
                    <div style={{ width: 48, height: 48, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,174,255,0.12)', color: 'var(--dp-blue-500)', fontWeight: 800, fontSize: 19 }}>{(it.channel_title || '?').trim().charAt(0).toUpperCase()}</div>
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--dp-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.channel_title || it.channel_id}</h3>
                      {it.idioma && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--dp-text-secondary)', background: 'var(--dp-bg-primary)', border: '1px solid var(--dp-border-subtle)', padding: '1px 6px', borderRadius: 999, textTransform: 'uppercase', flexShrink: 0 }}>{it.idioma.toUpperCase()}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--dp-text-tertiary)', marginTop: 2 }}>{fmtDate(it.created_at)}</div>
                  </div>
                  <button
                    onClick={(e) => hide(it.job_id, e)}
                    title={t('dobraNicho.delete_analysis')}
                    aria-label={t('dobraNicho.delete_analysis')}
                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dp-red-500)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-tertiary)'; }}
                    style={{ flexShrink: 0, background: 'transparent', border: 'none', borderRadius: 6, padding: 6, color: 'var(--dp-text-tertiary)', cursor: 'pointer', display: 'flex', transition: 'color 0.15s' }}
                  ><Trash2 size={14} /></button>
                </div>

                {it.nicho_principal && (
                  <div style={{ fontSize: 12.5, color: 'var(--dp-text-primary)', lineHeight: 1.4, marginBottom: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    <span style={{ color: 'var(--dp-blue-500)', fontWeight: 600 }}>{t('dobraNicho.dna_main_niche')}:</span> {it.nicho_principal}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', paddingTop: 12, borderTop: '1px solid var(--dp-border-subtle)' }}>
                  <Stat Icon={Users} value={fmtNum(it.subscriber_count)} label={t('dobraNicho.dna_subs')} />
                  <Stat Icon={Video} value={fmtNum(it.video_count)} label={t('dobraNicho.dna_published')} />
                  <Stat Icon={Flame} value={it.outliers_count ?? '—'} label={t('dobraNicho.dna_outliers')} />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ Icon, value, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <Icon size={15} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--dp-text-primary)', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 9.5, color: 'var(--dp-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}
